import { ActivityType } from './activity.types';

export type IdeaStopType = ActivityType;

export const IDEA_STOP_TYPE_OPTIONS: IdeaStopType[] = [
  'Flight',
  'Hotel',
  'Activity',
  'CarRental',
  'Concert',
  'Theatre',
  'Meeting',
  'Shopping',
  'Restaurant',
  'Tour',
  'Parking',
  'Directions',
  'BorderCrossing',
  'Ferry',
  'Cruise',
  'Rail',
  'Transportation',
];

interface AddressData {
  name: string;
  latitude: number;
  longitude: number;
}

interface BaseIdeaStopData {
  description: string;
  start_date_time: string;
  email?: string;
  phone?: string;
  link?: string;
  notes?: string;
}

export interface FlightIdeaStopData extends BaseIdeaStopData {
  flight_from: string;
  flight_to: string;
  departure_date_time?: string;
  arrival_date_time?: string;
  airline?: string;
  airline_iata_code?: string;
  flight_number?: string;
}

export interface HotelIdeaStopData extends BaseIdeaStopData {
  name: string;
  check_in_date_time?: string;
  check_out_date_time?: string;
  address?: AddressData;
}

export interface ActivityIdeaStopData extends BaseIdeaStopData {
  name: string;
  end_date_time?: string;
  address?: AddressData;
  place?: string;
}

export interface CarRentalIdeaStopData extends BaseIdeaStopData {
  end_date_time?: string;
  transport_name?: string;
  address?: AddressData;
}

export interface CulturalEventIdeaStopData extends BaseIdeaStopData {
  name: string;
  end_date_time?: string;
  place?: string;
  address?: AddressData;
}

export interface OutingIdeaStopData extends BaseIdeaStopData {
  name?: string;
  end_date_time?: string;
  place?: string;
  address?: AddressData;
}

export interface ParkingIdeaStopData extends BaseIdeaStopData {
  place?: string;
  end_date_time?: string;
  address?: AddressData;
}

export interface DirectionsIdeaStopData extends BaseIdeaStopData {
  place?: string;
  end_date_time?: string;
  address?: AddressData;
}

export interface BorderCrossingIdeaStopData extends BaseIdeaStopData {
  country_from: string; 
  country_to: string; 
  border_checkpoint?: string;
  address?: AddressData;
}

export interface FerryIdeaStopData extends BaseIdeaStopData {
  name: string;
  departure_date_time?: string;
  arrival_date_time?: string;
  location_from_name?: string;
  location_to_name?: string;
  departure_address?: AddressData;
  arrival_address?: AddressData;
}

export interface CruiseIdeaStopData extends BaseIdeaStopData {
  name: string; 
  departure_date_time?: string;
  arrival_date_time?: string;
  transport_name?: string; 
  location_from_name?: string;
  location_to_name?: string;
  starting_port_address?: AddressData;
  ending_port_address?: AddressData;
}

export interface RailIdeaStopData extends BaseIdeaStopData {
  name: string; 
  departure_date_time?: string;
  arrival_date_time?: string;
  location_from_name?: string;
  location_to_name?: string;
  departure_address?: AddressData;
  arrival_address?: AddressData;
}

export interface TransportationIdeaStopData extends BaseIdeaStopData {
  vehicle_type: string;
  departure_date_time?: string;
  arrival_date_time?: string;
  location_from_name?: string;
  location_to_name?: string;
  departure_address?: AddressData;
  arrival_address?: AddressData;
}

export type IdeaStopDataUnion =
  | FlightIdeaStopData
  | HotelIdeaStopData
  | ActivityIdeaStopData
  | CarRentalIdeaStopData
  | CulturalEventIdeaStopData
  | OutingIdeaStopData
  | ParkingIdeaStopData
  | DirectionsIdeaStopData
  | BorderCrossingIdeaStopData
  | FerryIdeaStopData
  | CruiseIdeaStopData
  | RailIdeaStopData
  | TransportationIdeaStopData;

export interface CreateIdeaStopFormData {
  ideaStopType: IdeaStopType;

  description: string;

  name: string;
  place: string;

  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  departureDate: string;
  departureTime: string;
  arrivalDate: string;
  arrivalTime: string;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;

  flightFrom: string;
  flightTo: string;
  airline: string;
  flightNumber: string;

  vehicleType: string;
  transportName: string;

  departureLocation: string;
  arrivalLocation: string;
  startingPortName: string;
  endingPortName: string;
  departureStation: string;
  arrivalStation: string;

  countryFrom: string;
  countryTo: string;
  borderCheckpoint: string;

  address: string;
  departureAddress: string;
  arrivalAddress: string;
  startingPortAddress: string;
  endingPortAddress: string;

  shipName: string;

  phone: string;
  email: string;
  link: string;
  notes: string;

  address_details?: google.maps.places.PlaceResult;
  departureAddress_details?: google.maps.places.PlaceResult;
  arrivalAddress_details?: google.maps.places.PlaceResult;
  startingPortAddress_details?: google.maps.places.PlaceResult;
  endingPortAddress_details?: google.maps.places.PlaceResult;

  flightFrom_details?: { name: string; iata_code: string };
  flightTo_details?: { name: string; iata_code: string };
  airline_details?: { name: string; iata_code: string };
}

export interface IdeaStop {
  id: number;
  idea: number;
  stop_type: IdeaStopType;
  description: string;
  start_date_time: string;
  email?: string;
  phone?: string;
  link?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  custom_cover?: string | null;
  [key: string]: any;
}

export interface CreateIdeaStopPayload {
  ideaId: number;
  ideaStopType: IdeaStopType;
  ideaStopData: IdeaStopDataUnion & { idea: number };
}

export interface UpdateIdeaStopPayload {
  id: number;
  data: Partial<IdeaStopDataUnion>;
}

export const getIdeaStopTypeLabel = (apiType: IdeaStopType): string => {
  const mapping: Record<IdeaStopType, string> = {
    Flight: 'Flight',
    Hotel: 'Hotel',
    Activity: 'Activity',
    CarRental: 'Car Rental',
    Concert: 'Concert',
    Theatre: 'Theatre',
    Meeting: 'Meeting',
    Shopping: 'Shopping',
    Restaurant: 'Restaurant',
    Tour: 'Tour',
    Parking: 'Parking',
    Directions: 'Directions',
    BorderCrossing: 'Border Crossing',
    Ferry: 'Ferry',
    Cruise: 'Cruise',
    Rail: 'Rail',
    Transportation: 'Transportation',
  };
  return mapping[apiType] || apiType;
};
